// src/pages/credenciamento/ImpressaoLote.tsx
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Progress,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react'
import QRCode from 'qrcode'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FiSearch } from 'react-icons/fi'
import api from '../../utils/api'

interface GroupItem {
  value: string
  count: number
}

interface UserForPrint {
  uid: string
  name: string
  cpf: string
  customFields: Record<string, any>
  registrationUid: string
  checkinCount: number
}

/**
 * Agrupamentos oferecidos, na ordem em que servem para imprimir em lote.
 *
 * A lista anterior tinha seis opções e TRÊS estavam mortas nos dados deste evento:
 * "Tipo de Ingresso" (que era o padrão, e cujo dado vive na coleção tickets, não no
 * usuário), "Nome Fantasia" e "Categoria" — as três devolviam lista vazia. Quem abria a
 * tela via nada e concluía que a impressão em lote não funcionava.
 *
 * "Empresa" continua, mas rendeu 329 grupos para 570 pessoas, 245 deles com uma pessoa só,
 * porque muita gente digitou o próprio CPF no campo. Fica por último e com aviso.
 *
 * `dica` explica para que serve cada corte — sem isso o operador escolhe por adivinhação.
 */
const CAMPOS: Array<{ chave: string; rotulo: string; dica: string }> = [
  { chave: 'tipoIngresso', rotulo: 'Tipo de ingresso', dica: 'Médico associado, não médico, sênior…' },
  { chave: 'conselhoProfissional', rotulo: 'Conselho profissional', dica: 'Medicina, enfermagem, farmácia…' },
  { chave: 'estadoUf', rotulo: 'Estado', dica: 'Útil para separar por delegação' },
  { chave: 'cnpj', rotulo: 'CNPJ da empresa', dica: 'Inscrições feitas pela empresa' },
  { chave: 'tamanhoCamiseta', rotulo: 'Tamanho da camiseta', dica: 'Para separar o brinde junto do crachá' },
  { chave: 'associadoSbam', rotulo: 'Associado da SBAM', dica: 'Sim ou não' },
  { chave: 'empresa', rotulo: 'Empresa (texto livre)', dica: 'Preenchido pelo participante — muitos grupos de 1' },
]

const FIELD_LABELS: Record<string, string> = Object.fromEntries(
  CAMPOS.map(c => [c.chave, c.rotulo]),
)

/**
 * Como o valor do grupo aparece na tela.
 *
 * Nem todo campo é texto: "É associado da SBAM" é booleano e chegava aqui como "true".
 * O valor cru continua sendo o que vai para a API — só a leitura muda.
 */
const rotuloDoValor = (v: string) => (v === 'true' ? 'Sim' : v === 'false' ? 'Não' : v)

const ImpressaoLote: React.FC = () => {
  const toast = useToast()
  const [field, setField] = useState(CAMPOS[0].chave)
  const [groups, setGroups] = useState<GroupItem[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set())
  const [usersToprint, setUsersToPrint] = useState<UserForPrint[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [printing, setPrinting] = useState(false)
  const [printProgress, setPrintProgress] = useState({ current: 0, total: 0 })
  const [step, setStep] = useState<'select' | 'confirm' | 'printing'>('select')
  /*
    Check-in do lote.

    Imprimir em lote ENTREGA credencial, e entrega de credencial e check-in. Sem isto,
    quem passava pela impressao em lote nao contava em dia nenhum — e a contagem de
    "credenciados hoje" nascia com um buraco do tamanho do lote.

    Fica como pergunta, e nao automatico: reimprimir uma etiqueta perdida tambem passa por
    aqui, e nesse caso um check-in novo seria presenca inventada.
  */
  const [perguntarCheckin, setPerguntarCheckin] = useState(false)
  const [lotePendente, setLotePendente] = useState<UserForPrint[]>([])
  const [checkinEmCurso, setCheckinEmCurso] = useState(false)
  const [checkinProgresso, setCheckinProgresso] = useState({ atual: 0, total: 0 })
  const cancelarCheckinRef = useRef<HTMLButtonElement>(null!)
  const [searchTerm, setSearchTerm] = useState('')

  // Filtro da lista de grupos
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return groups
    const term = searchTerm.trim().toLowerCase()
    return groups.filter(g => g.value.toLowerCase().includes(term))
  }, [groups, searchTerm])

  // Buscar agrupamentos
  const fetchGroups = useCallback(async () => {
    setLoadingGroups(true)
    try {
      const res = await api.get('/users/group-by-field', { params: { field } })
      setGroups(res.data || [])
    } catch (err: any) {
      toast({ title: 'Erro ao buscar agrupamentos', description: err.message, status: 'error', duration: 3000 })
    } finally {
      setLoadingGroups(false)
    }
  }, [field, toast])

  useEffect(() => {
    fetchGroups()
  }, [fetchGroups])

  // Selecionar/deselecionar grupo
  const toggleGroup = (value: string) => {
    setSelectedValues(prev => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }

  const selectAll = () => {
    const visible = filteredGroups.map(g => g.value)
    const allVisibleSelected = visible.every(v => selectedValues.has(v))
    if (allVisibleSelected) {
      setSelectedValues(prev => {
        const next = new Set(prev)
        visible.forEach(v => next.delete(v))
        return next
      })
    } else {
      setSelectedValues(prev => {
        const next = new Set(prev)
        visible.forEach(v => next.add(v))
        return next
      })
    }
  }

  const totalSelected = groups
    .filter(g => selectedValues.has(g.value))
    .reduce((sum, g) => sum + g.count, 0)

  // Carregar usuários para impressão
  const loadUsersForPrint = async () => {
    if (selectedValues.size === 0) return
    setLoadingUsers(true)
    setStep('confirm')
    try {
      const allUsers: UserForPrint[] = []
      for (const val of Array.from(selectedValues)) {
        const res = await api.get('/users/by-field', { params: { field, value: val } })
        const users = (res.data || []).filter((u: UserForPrint) => u.registrationUid)
        allUsers.push(...users)
      }
      setUsersToPrint(allUsers)
    } catch (err: any) {
      toast({ title: 'Erro ao buscar usuários', description: err.message, status: 'error', duration: 3000 })
      setStep('select')
    } finally {
      setLoadingUsers(false)
    }
  }

  // Impressão em lote — gera todas as etiquetas num único documento
  const executePrint = async () => {
    if (usersToprint.length === 0) return
    setPrinting(true)
    setStep('printing')
    setPrintProgress({ current: 0, total: usersToprint.length })

    try {
      // O HTML vem do servidor, do MESMO template da etiqueta individual.
      //
      // Antes era montado aqui, em 90x50mm, com nome/empresa/cargo — campos de outro
      // evento. O crachá configurado é 82x30mm com nome, conselho e estado. Quem imprimia
      // em lote recebia uma etiqueta diferente de quem imprimia individualmente, no mesmo
      // balcão e no mesmo dia.
      const codes = usersToprint.map(u => u.registrationUid).filter(Boolean)
      setPrintProgress({ current: 0, total: codes.length })
      const resp = await api.post('/badge/lote', { codes }, { responseType: 'text' })
      const labelsHtml: string = typeof resp.data === 'string' ? resp.data : String(resp.data)
      setPrintProgress({ current: codes.length, total: codes.length })

      // Usa iframe oculto para imprimir
      let printFrame = document.getElementById('batch-lote-frame') as HTMLIFrameElement
      if (printFrame) printFrame.remove()

      printFrame = document.createElement('iframe')
      printFrame.id = 'batch-lote-frame'
      printFrame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
      document.body.appendChild(printFrame)

      const doc = printFrame.contentWindow?.document
      if (!doc) throw new Error('Não foi possível acessar o iframe')

      doc.open()
      doc.write(labelsHtml)
      doc.close()

      await new Promise<void>((resolve, reject) => {
        printFrame.contentWindow?.addEventListener('load', () => {
          setTimeout(() => {
            try {
              printFrame.contentWindow?.focus()
              printFrame.contentWindow?.print()
              resolve()
            } catch (e) { reject(e) }
          }, 500)
        })
        // fallback timeout
        setTimeout(resolve, 5000)
      })

      toast({ title: `${usersToprint.length} etiquetas enviadas para impressão`, status: 'success', duration: 4000 })

      // A pergunta vem DEPOIS da impressao sair: se o papel travar, o operador cancela e
      // ninguem fica com check-in de credencial que nao foi entregue.
      setLotePendente(usersToprint)
      setPerguntarCheckin(true)
    } catch (err: any) {
      toast({ title: 'Erro ao imprimir', description: err.message, status: 'error', duration: 5000 })
    } finally {
      setPrinting(false)
      setStep('select')
      setPrintProgress({ current: 0, total: 0 })
    }
  }

  /**
   * Check-in de todo o lote, um a um.
   *
   * Sequencial de proposito: sao ate algumas centenas de chamadas, e disparar todas de uma
   * vez sobrecarrega o servidor no meio do balcao. Quem falhar e listado pelo nome — o
   * operador precisa saber QUEM refazer, nao so que "houve erro".
   */
  const confirmarCheckinDoLote = async () => {
    const alvo = lotePendente.filter(u => u.registrationUid)
    setCheckinEmCurso(true)
    setCheckinProgresso({ atual: 0, total: alvo.length })

    const falhas: string[] = []
    for (let i = 0; i < alvo.length; i++) {
      const u = alvo[i]
      try {
        await api.post('/checkin', { registration: u.registrationUid, room: null })
      } catch {
        falhas.push(u.name)
      }
      setCheckinProgresso({ atual: i + 1, total: alvo.length })
    }

    setCheckinEmCurso(false)
    setPerguntarCheckin(false)
    setLotePendente([])

    if (falhas.length) {
      toast({
        title: `${alvo.length - falhas.length} de ${alvo.length} com check-in`,
        description: `Falhou em: ${falhas.slice(0, 5).join(', ')}${falhas.length > 5 ? ` e mais ${falhas.length - 5}` : ''}`,
        status: 'warning',
        duration: 9000,
        isClosable: true,
      })
    } else {
      toast({
        title: `Check-in realizado para ${alvo.length} pessoa(s)`,
        status: 'success',
        duration: 4000,
      })
    }
  }

  return (
    <Box>
      <Heading size="md" mb={4}>Impressão de Etiquetas em Lote</Heading>

      {step === 'select' && (
        <>
          <Flex mb={3} gap={3} align="center" wrap="wrap">
            <Text fontWeight="bold">Agrupar por:</Text>
            <Select value={field} w="260px" size="sm" onChange={e => { setField(e.target.value); setSelectedValues(new Set()); setGroups([]); }}>
              {CAMPOS.map(c => (
                <option key={c.chave} value={c.chave}>{c.rotulo}</option>
              ))}
            </Select>
            <Button size="sm" colorScheme="blue" onClick={fetchGroups} isLoading={loadingGroups}>
              Atualizar
            </Button>
            <Text fontSize="sm" color="gray.500">
              {CAMPOS.find(c => c.chave === field)?.dica}
            </Text>
          </Flex>

          {loadingGroups ? (
            <Flex justify="center" py={8}><Spinner size="lg" /></Flex>
          ) : groups.length === 0 ? (
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Text fontSize="sm">
                Nenhum participante tem <b>{FIELD_LABELS[field] || field}</b> preenchido.
                Escolha outro agrupamento.
              </Text>
            </Alert>
          ) : (
            <>
              <InputGroup mb={3} maxW="400px">
                <InputLeftElement pointerEvents="none">
                  <FiSearch color="gray" />
                </InputLeftElement>
                <Input
                  placeholder={`Buscar ${FIELD_LABELS[field] || field}...`}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  size="md"
                />
              </InputGroup>

              <Flex mb={3} justify="space-between" align="center">
                <Checkbox
                  isChecked={filteredGroups.length > 0 && filteredGroups.every(g => selectedValues.has(g.value))}
                  isIndeterminate={filteredGroups.some(g => selectedValues.has(g.value)) && !filteredGroups.every(g => selectedValues.has(g.value))}
                  onChange={selectAll}
                >
                  Selecionar todos ({filteredGroups.length}{searchTerm ? ` de ${groups.length}` : ''})
                </Checkbox>
                {selectedValues.size > 0 && (
                  <Badge colorScheme="blue" fontSize="md" px={3} py={1} borderRadius="md">
                    {totalSelected} etiqueta{totalSelected !== 1 ? 's' : ''} para imprimir
                  </Badge>
                )}
              </Flex>

              <Box maxH="400px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
                <Table size="sm" variant="striped">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th w="40px"></Th>
                      <Th>{FIELD_LABELS[field] || field}</Th>
                      <Th isNumeric w="100px">Qtd</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredGroups.map(g => (
                      <Tr
                        key={g.value}
                        cursor="pointer"
                        onClick={() => toggleGroup(g.value)}
                        _hover={{ bg: 'blue.50' }}
                      >
                        <Td>
                          <Checkbox
                            isChecked={selectedValues.has(g.value)}
                            onChange={() => toggleGroup(g.value)}
                            onClick={e => e.stopPropagation()}
                          />
                        </Td>
                        <Td fontWeight={selectedValues.has(g.value) ? 'bold' : 'normal'}>
                          {rotuloDoValor(g.value)}
                        </Td>
                        <Td isNumeric>
                          <Badge colorScheme="purple">{g.count}</Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <Divider my={4} />

              <Button
                colorScheme="green"
                size="lg"
                onClick={loadUsersForPrint}
                isDisabled={selectedValues.size === 0}
                w={{ base: 'full', md: 'auto' }}
              >
                Preparar Impressão ({totalSelected} etiquetas)
              </Button>
            </>
          )}
        </>
      )}

      {step === 'confirm' && (
        <VStack spacing={4} align="stretch">
          {loadingUsers ? (
            <Flex justify="center" py={8} direction="column" align="center" gap={3}>
              <Spinner size="lg" />
              <Text>Carregando usuários...</Text>
            </Flex>
          ) : (
            <>
              <Alert status="info">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">{usersToprint.length} etiquetas prontas para impressão</Text>
                  <Text fontSize="sm">
                    Grupos selecionados: {Array.from(selectedValues).map(rotuloDoValor).join(', ')}
                  </Text>
                </Box>
              </Alert>

              <Box maxH="300px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md">
                <Table size="sm" variant="striped">
                  <Thead position="sticky" top={0} bg="white" zIndex={1}>
                    <Tr>
                      <Th>#</Th>
                      <Th>Nome Crachá</Th>
                      <Th>{FIELD_LABELS[field] || field}</Th>
                      <Th>Cargo</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {usersToprint.map((u, i) => (
                      <Tr key={u.uid}>
                        <Td>{i + 1}</Td>
                        <Td>{u.customFields?.nomeNoCracha || u.name}</Td>
                        <Td>{u.customFields?.[field] || ''}</Td>
                        <Td>{u.customFields?.cargo || ''}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <HStack spacing={3}>
                <Button colorScheme="gray" onClick={() => setStep('select')}>
                  Voltar
                </Button>
                <Button
                  colorScheme="green"
                  size="lg"
                  onClick={executePrint}
                  isLoading={printing}
                >
                  Confirmar e Imprimir ({usersToprint.length} etiquetas)
                </Button>
              </HStack>
            </>
          )}
        </VStack>
      )}

      {step === 'printing' && (
        <VStack spacing={4} py={8}>
          <Spinner size="xl" color="blue.500" />
          <Text fontWeight="bold" fontSize="lg">Preparando etiquetas...</Text>
          <Box w="100%" maxW="400px">
            <Progress
              value={(printProgress.current / Math.max(printProgress.total, 1)) * 100}
              size="lg"
              colorScheme="blue"
              borderRadius="md"
              hasStripe
              isAnimated
            />
            <Text textAlign="center" mt={1} fontSize="sm" color="gray.600">
              {printProgress.current} / {printProgress.total} QR Codes gerados
            </Text>
          </Box>
        </VStack>
      )}

      {/*
        Mesma pergunta da impressao individual, aplicada ao lote inteiro. O texto diz o
        numero de pessoas porque a decisao muda conforme o tamanho: confirmar 3 e trivial,
        confirmar 200 e um ato de operacao.
      */}
      <AlertDialog
        isOpen={perguntarCheckin}
        leastDestructiveRef={cancelarCheckinRef}
        onClose={() => { if (!checkinEmCurso) { setPerguntarCheckin(false); setLotePendente([]) } }}
        closeOnOverlayClick={!checkinEmCurso}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Realizar Check-in do lote?
            </AlertDialogHeader>
            <AlertDialogBody>
              {checkinEmCurso ? (
                <>
                  <Text mb={2}>
                    Registrando check-in… {checkinProgresso.atual} de {checkinProgresso.total}
                  </Text>
                  <Progress
                    value={checkinProgresso.total ? (checkinProgresso.atual / checkinProgresso.total) * 100 : 0}
                    size="sm"
                    colorScheme="green"
                    borderRadius="md"
                  />
                </>
              ) : (
                <>
                  As etiquetas foram enviadas para impressão. Deseja registrar o{' '}
                  <strong>check-in</strong> das{' '}
                  <strong>{lotePendente.filter(u => u.registrationUid).length} pessoa(s)</strong>{' '}
                  deste lote?
                  <Text fontSize="sm" color="gray.600" mt={3}>
                    Escolha <strong>Não</strong> se foi apenas reimpressão de etiqueta — nesse
                    caso o check-in já foi feito antes e não deve contar de novo.
                  </Text>
                </>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelarCheckinRef}
                onClick={() => { setPerguntarCheckin(false); setLotePendente([]) }}
                isDisabled={checkinEmCurso}
              >
                Não, apenas imprimi
              </Button>
              <Button
                colorScheme="green"
                ml={3}
                onClick={confirmarCheckinDoLote}
                isLoading={checkinEmCurso}
                loadingText="Registrando…"
              >
                Sim, fazer check-in
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default ImpressaoLote
